/**
 * ─────────────────────────────────────────────
 * src/services/kanbanService.js
 * ─────────────────────────────────────────────
 *
 * Covers all list, task, subtask, and comment operations.
 *
 * Routes covered:
 *   GET    /api/kanban                                         → kanban project list
 *   POST   /api/projects/:id/lists                            → create list
 *   POST   /api/projects/:id/lists/:lid/tasks                 → create task
 *   PUT    /api/projects/:id/lists/:lid/tasks/:tid            → edit task
 *   DELETE /api/projects/:id/lists/:lid/tasks/:tid            → delete task
 *   PATCH  /api/projects/:id/lists/:lid/tasks/:tid/status     → toggle status
 *   POST   /api/projects/:id/lists/:lid/tasks/:tid/subtasks   → add subtask
 *   PATCH  /api/.../subtasks/:sid/toggle                      → toggle subtask
 *   POST   /api/.../tasks/:tid/comments                       → post comment (with files)
 */

import api from "../lib/api";

// ─────────────────────────────────────────────
// GET KANBAN PROJECTS
// Returns: { success, count, data: [...projects] }
// ─────────────────────────────────────────────
const getKanbanProjects = async () => {
  const response = await api.get("/kanban");
  return response.data;
};

// ─────────────────────────────────────────────
// CREATE LIST
// Payload: { name, scope?, duration?, status? }
// Returns: { success, data: list }
// ─────────────────────────────────────────────
const createList = async (projectId, listData) => {
  const payload = typeof listData === "string" ? { name: listData } : listData;
  const response = await api.post(`/projects/${projectId}/lists`, payload);
  return response.data;
};

const updateList = async (projectId, listId, listData) => {
  const response = await api.put(`/projects/${projectId}/lists/${listId}`, listData);
  return response.data;
};

const deleteList = async (projectId, listId) => {
  const response = await api.delete(`/projects/${projectId}/lists/${listId}`);
  return response.data;
};

// ─────────────────────────────────────────────
// CREATE TASK
// Payload: { name, taskType, priority, estimatedHours,
//            taskStartDate, taskDeadline, assignees, description }
// Returns: { success, data: task (populated) }
// ─────────────────────────────────────────────
const createTask = async (projectId, listId, taskData) => {
  const response = await api.post(
    `/projects/${projectId}/lists/${listId}/tasks`,
    taskData,
  );
  return response.data;
};

// ─────────────────────────────────────────────
// EDIT TASK
// Returns: { success, data: task (populated) }
// ─────────────────────────────────────────────
const editTask = async (projectId, listId, taskId, taskData) => {
  const response = await api.put(
    `/projects/${projectId}/lists/${listId}/tasks/${taskId}`,
    taskData,
  );
  return response.data;
};

const updateSubtask = async (projectId, listId, taskId, subtaskId, title) => {
  const response = await api.put(
    `/projects/${projectId}/lists/${listId}/tasks/${taskId}/subtasks/${subtaskId}`,
    { title },
  );

  return response.data;
};

const deleteSubtask = async (projectId, listId, taskId, subtaskId) => {
  return api.delete(
    `/projects/${projectId}/lists/${listId}/tasks/${taskId}/subtasks/${subtaskId}`,
  );
};

// ─────────────────────────────────────────────
// ADD SUBTASK ATTACHMENT(S)
// Payload: FormData with one or more files under the "files" field
// (backend: subtaskUpload.array("files", 5), 20MB max per file)
// Returns: { success, data: subtask (with populated attachments) }
// ─────────────────────────────────────────────
const addSubtaskAttachment = async (
  projectId,
  listId,
  taskId,
  subtaskId,
  formData,
) => {
  const response = await api.post(
    `/projects/${projectId}/lists/${listId}/tasks/${taskId}/subtasks/${subtaskId}/attachments`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return response.data;
};

// ─────────────────────────────────────────────
// DELETE SUBTASK ATTACHMENT
// Only the uploader or an admin may delete (enforced server-side)
// Returns: { success, data: subtask }
// ─────────────────────────────────────────────
const deleteSubtaskAttachment = async (
  projectId,
  listId,
  taskId,
  subtaskId,
  attachmentId,
) => {
  const response = await api.delete(
    `/projects/${projectId}/lists/${listId}/tasks/${taskId}/subtasks/${subtaskId}/attachments/${attachmentId}`,
  );
  return response.data;
};

// ─────────────────────────────────────────────
// DELETE TASK
// Returns: { success, message }
// ─────────────────────────────────────────────
const deleteTask = async (projectId, listId, taskId) => {
  const response = await api.delete(
    `/projects/${projectId}/lists/${listId}/tasks/${taskId}`,
  );
  return response.data;
};

// ─────────────────────────────────────────────
// TOGGLE TASK STATUS (todo ↔ done)
// Returns: { success, data: task (populated) }
// ─────────────────────────────────────────────
const toggleTaskStatus = async (projectId, listId, taskId) => {
  const response = await api.patch(
    `/projects/${projectId}/lists/${listId}/tasks/${taskId}/status`,
  );
  return response.data;
};

// ─────────────────────────────────────────────
// ADD SUBTASK
// Payload: { title }
// Returns: { success, data: { subtask, subtasksTotal, subtasksDone } }
// ─────────────────────────────────────────────
const addSubtask = async (projectId, listId, taskId, title) => {
  const response = await api.post(
    `/projects/${projectId}/lists/${listId}/tasks/${taskId}/subtasks`,
    { title },
  );
  return response.data;
};

// ─────────────────────────────────────────────
// TOGGLE SUBTASK
// Returns: { success, data: { subtask, subtasksTotal, subtasksDone } }
// ─────────────────────────────────────────────
const toggleSubtask = async (projectId, listId, taskId, subtaskId) => {
  const response = await api.patch(
    `/projects/${projectId}/lists/${listId}/tasks/${taskId}/subtasks/${subtaskId}/toggle`,
  );
  return response.data;
};

// ─────────────────────────────────────────────
// POST COMMENT
// Supports both JSON (text only) and FormData (with files)
// The caller decides which format to pass:
//   - payload is FormData  → files attached
//   - payload is plain obj → JSON only
// Returns: { success, data: comment }
// ─────────────────────────────────────────────
const postComment = async (projectId, listId, taskId, payload) => {
  const isFormData = payload instanceof FormData;
  const response = await api.post(
    `/projects/${projectId}/lists/${listId}/tasks/${taskId}/comments`,
    payload,
    // When sending FormData, let browser set Content-Type (multipart boundary)
    // axios handles this automatically when payload is FormData
    isFormData ? { headers: { "Content-Type": "multipart/form-data" } } : {},
  );
  return response.data;
};

const updateComment = async (projectId, listId, taskId, commentId, payload) => {
  const response = await api.put(
    `/projects/${projectId}/lists/${listId}/tasks/${taskId}/comments/${commentId}`,
    payload,
  );

  return response.data;
};

const deleteComment = async (projectId, listId, taskId, commentId) => {
  const response = await api.delete(
    `/projects/${projectId}/lists/${listId}/tasks/${taskId}/comments/${commentId}`,
  );

  return response.data;
};

// ─────────────────────────────────────────────
// MOVE TASK (drag & drop)
// Moves a card within its list or into another list and persists the
// resulting order. toIndex is the destination slot, 0-based.
// Returns: { success, data: { taskId, fromListId, toListId, toIndex } }
// ─────────────────────────────────────────────
const moveTask = async (projectId, { taskId, toListId, toIndex }) => {
  const response = await api.put(`/projects/${projectId}/lists/move-task`, {
    taskId,
    toListId,
    toIndex,
  });
  return response.data;
};

// ─────────────────────────────────────────────
// REORDER LISTS (drag & drop)
// listIds must be the full ordered set of the board's lists.
// Returns: { success, data: { listIds } }
// ─────────────────────────────────────────────
const reorderLists = async (projectId, listIds) => {
  const response = await api.put(`/projects/${projectId}/lists/reorder`, {
    listIds,
  });
  return response.data;
};

const kanbanService = {
  getKanbanProjects,
  createList,
  updateList,
  deleteList,
  moveTask,
  reorderLists,
  createTask,
  editTask,
  deleteTask,
  toggleTaskStatus,
  addSubtask,
  toggleSubtask,
  postComment,
  updateComment,
  deleteComment,
  updateSubtask,
  deleteSubtask,
  addSubtaskAttachment,
  deleteSubtaskAttachment,
};

export default kanbanService;
